import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  useMediaQuery,
  useTheme,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Link,
} from '@mui/material';
import { getActiveRaceCards, cleanupRaceCardListeners, applyToRaceCard, deleteApplicationForRaceCard } from '../services/raceCardService';
import { getUserProfile } from '../services/userProfileService';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { teamColors } from '../config/teamColors';
import { ALL_TRACK_OPTIONS, GAME_VERSIONS } from '../config/f1GameTracks';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import styles from '../styles/RaceCards.module.css';
import ShareButton from '../components/ShareButton';
import { trackEvent } from '../utils/analytics';

const externalInviteHref = (url) => {
  const s = String(url || '').trim();
  if (!s) return '#';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, '')}`;
};

// Helper function to get timezone abbreviation from region name
const getRegionAbbreviation = (region) => {
  switch (region) {
    case 'North America':
      return 'ET'; // Eastern Time
    case 'Europe':
      return 'GMT'; // Greenwich Mean Time
    case 'Asia':
      return 'SGT'; // Singapore Time
    case 'Australia':
      return 'AET'; // Australian Eastern Time
    case 'South America':
      return 'BRT'; // Brasilia Time
    default:
      return 'GMT';
  }
};

// Helper function to convert 24-hour time to 12-hour format with AM/PM and timezone
const formatTimeWithAMPM = (timeString, region) => {
  if (!timeString) return 'Unknown';
  
  // Check if it's already in 12-hour format with AM/PM
  if (timeString.includes('AM') || timeString.includes('PM')) {
    // If it already has AM/PM but no timezone, add the timezone
    if (region && !timeString.includes('(')) {
      const timezoneAbbr = getRegionAbbreviation(region);
      return `${timeString} (${timezoneAbbr})`;
    }
    return timeString;
  }
  
  // Parse 24-hour format (HH:MM)
  const [hours, minutes] = timeString.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) {
    return timeString; // Return original if parsing fails
  }
  
  const hour12 = ((hours + 11) % 12) + 1;
  const ampm = hours < 12 ? 'AM' : 'PM';
  const minutesStr = minutes.toString().padStart(2, '0');
  
  const formattedTime = `${hour12}:${minutesStr} ${ampm}`;
  
  // Add timezone abbreviation if region is provided
  if (region) {
    const timezoneAbbr = getRegionAbbreviation(region);
    return `${formattedTime} (${timezoneAbbr})`;
  }
  
  return formattedTime;
};

// Helper function to generate SEO-friendly alt text for race card images
const getRaceCardImageAlt = (card) => {
  if (!card) return 'F1 Game Race Card';
  
  const gameLabel = card.gameVersion ? GAME_VERSIONS.find(g => g.value === card.gameVersion)?.label || card.gameVersion : 'F1 26';
  if (card.cardType === 'career') {
    const team = card.teamPreference || 'Any team';
    const rivalry = card.rivalryType || 'Any rivalry';
    const aiLevel = card.aiLevel || '';
    return `${gameLabel} Career Mode Race Card - ${team} ${rivalry}${aiLevel ? ` at ${aiLevel} AI` : ''} - Find Racing Partner`;
  }
  
  if (card.cardType === 'leagueStartup') {
    const aiLevel = card.aiLevel || '';
    return `${gameLabel} League Startup Race Card${aiLevel ? (aiLevel === 'Multiple Tiers' ? ' - Multiple Tiers' : ` at ${aiLevel} AI`) : ''} - Join New League`;
  }
  
  if (card.cardType === 'unranked') {
    const carType = card.carType || 'F1';
    const track = card.raceDate ? ` on ${formatDateDDMMYYYY(card.raceDate.seconds ? card.raceDate.seconds * 1000 : card.raceDate)}` : '';
    return `${gameLabel} Unranked Lobby Race Card - ${carType}${track} - Join Casual Racing Session`;
  }
  
  if (card.cardType === 'reserve') {
    const track = card.track || 'Unknown Track';
    const aiLevel = card.estAiLevel || '';
    return `${gameLabel} Reserve Race Card - ${track}${aiLevel ? ` at ${aiLevel} AI` : ''} - Reserve Driver Opportunity`;
  }
  
  if (card.cardType === 'commentary') {
    const track = card.track || 'Unknown Track';
    return `${gameLabel} Commentary Card - ${track} - Find Race Commentator`;
  }
  
  // Fallback for legacy cards
  const team = card.teamPreference || 'Any team';
  const rivalry = card.rivalryType || 'Any rivalry';
  return `${gameLabel} Race Card - ${team} ${rivalry} - Find Racing Partner`;
};

// Helper function to get the appropriate race card image based on card type and fields
const getRaceCardImage = (card) => {
  console.log('Getting image for card:', card);
  
  // For career cards, use the existing logic
  if (card.cardType === 'career') {
    const rivalryType = card.rivalryType;
    const teamPreference = card.teamPreference;
    
    // Team name to abbreviation mapping
    const teamAbbreviations = {
      'Red Bull': 'rb',
      'Racing Bulls': 'visa',
      'Ferrari': 'ferrari',
      'McLaren': 'mclaren',
      'Mercedes': 'mercedes',
      'Aston Martin': 'aston',
      'Haas': 'haas',
      'Alpine': 'alpine',
      'Kick Sauber': 'sauber',
      'Williams': 'williams',
      'Any team': 'any'
    };
    
    // If a specific team is selected (not "Any team"), use team-specific image regardless of rivalry type
    if (teamPreference && teamPreference !== 'Any team') {
      const teamAbbr = teamAbbreviations[teamPreference] || 'any';
      return `/images/2-player-career/teammates-card-image-${teamAbbr}.jpg`;
    }
    
    // For "Any team" or no team preference, use rivalry-based logic
    switch (rivalryType) {
      case 'Teammates':
        // For "Any team + Teammates", show a specific teammates card image
        if (teamPreference === 'Any team') {
          return '/images/2-player-career/teammates-card-image-rb.jpg';
        }
        // For other cases, use the team abbreviation
        const teamAbbr = teamAbbreviations[teamPreference] || 'any';
        return `/images/2-player-career/teammates-card-image-${teamAbbr}.jpg`;
      case 'Rival Teams':
      case 'Rival teams':
      case 'rival teams':
      case 'Any rivalry':
      case 'Any Rivalry':
      case 'any rivalry':
      default:
        // For "Any team" with "Any rivalry", deterministically select from 3 options based on card ID
        if (teamPreference === 'Any team') {
          const rivalImages = [
            '/images/2-player-career/rival-teams-option-5.jpg',
            '/images/2-player-career/rival-teams-option-6.jpg',
            '/images/2-player-career/rival-teams-option-7.jpg'
          ];
          // Use card ID to deterministically select an image
          const cardId = card.id || card.title || 'default';
          const hash = cardId.split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
          }, 0);
          const imageIndex = Math.abs(hash) % rivalImages.length;
          return rivalImages[imageIndex];
        }
        // Fallback to option 6 for other cases
        return '/images/2-player-career/rival-teams-option-6.jpg';
    }
  }
  
  // For league startup cards, use preview image
  if (card.cardType === 'leagueStartup') {
    return '/images/2-player-career/preview-image.png';
  }
  
  // For unranked cards, use a generic unranked image
  if (card.cardType === 'unranked') {
    return '/images/unranked-lobby/unranked-lobby-image.jpg';
  }
  
  // For reserve cards, use a generic reserve image
  if (card.cardType === 'reserve') {
    // Check for specific track images
    if (card.track === 'Australia') {
      return '/images/reserve-a-race/australia-image.svg';
    }
    if (card.track === 'China') {
      return '/images/reserve-a-race/china-image.svg';
    }
    if (card.track === 'Japan') {
      return '/images/reserve-a-race/japan-image.svg';
    }
    if (card.track === 'Bahrain') {
      return '/images/reserve-a-race/bahrain-image.svg';
    }
    if (card.track === 'Saudi Arabia') {
      return '/images/reserve-a-race/saudi-arabia-image.svg';
    }
    if (card.track === 'Miami') {
      return '/images/reserve-a-race/miami-image.svg';
    }
    if (card.track === 'Imola') {
      return '/images/reserve-a-race/imola-image.svg';
    }
    if (card.track === 'Monaco') {
      return '/images/reserve-a-race/monaco-image.svg';
    }
    if (card.track === 'Spain') {
      return '/images/reserve-a-race/spain-image.svg';
    }
    if (card.track === 'Madrid') {
      return '/images/reserve-a-race/spain-image.svg';
    }
    if (card.track === 'Canada') {
      return '/images/reserve-a-race/canada-image.svg';
    }
    if (card.track === 'Austria') {
      return '/images/reserve-a-race/austria-image.svg';
    }
    if (card.track === 'Britain') {
      return '/images/reserve-a-race/britain-image.svg';
    }
    if (card.track === 'Belgium') {
      return '/images/reserve-a-race/belgium-image.svg';
    }
    if (card.track === 'Hungary') {
      return '/images/reserve-a-race/hungary-image.svg';
    }
    if (card.track === 'Netherlands') {
      return '/images/reserve-a-race/netherlands-image.svg';
    }
    if (card.track === 'Monza') {
      return '/images/reserve-a-race/monza-image.svg';
    }
    if (card.track === 'Azerbaijan') {
      return '/images/reserve-a-race/azerbaijan-image.svg';
    }
    if (card.track === 'Singapore') {
      return '/images/reserve-a-race/singapore-image.svg';
    }
    if (card.track === 'Austin') {
      return '/images/reserve-a-race/austin-image.svg';
    }
    if (card.track === 'Mexico') {
      return '/images/reserve-a-race/mexico-image.svg';
    }
    if (card.track === 'Brazil') {
      return '/images/reserve-a-race/brazil-image.svg';
    }
    if (card.track === 'Las Vegas') {
      return '/images/reserve-a-race/las-vegas-image.svg';
    }
    if (card.track === 'Qatar') {
      return '/images/reserve-a-race/qatar-image.svg';
    }
    if (card.track === 'Abu Dhabi') {
      return '/images/reserve-a-race/abu-dhabi-image.svg';
    }
    if (card.track === 'Austria (Reverse)') {
      return '/images/reserve-a-race/austria-reverse-image.svg';
    }
    if (card.track === 'Britain (Reverse)') {
      return '/images/reserve-a-race/britain-reverse-image.svg';
    }
    if (card.track === 'Netherlands (Reverse)') {
      return '/images/reserve-a-race/netherlands-reverse-image.svg';
    }
    if (card.track === 'Portugal') {
      return '/images/reserve-a-race/portugal-image.svg';
    }
    if (card.track === 'France') {
      return '/images/reserve-a-race/france-image.svg';
    }
    if (card.track === 'Russia') {
      return '/images/reserve-a-race/russia-image.svg';
    }
    return '/images/reserve-option-2.jpg';
  }
  // For commentary cards, use commentary image
  if (card.cardType === 'commentary') {
    return '/images/commentary/commentary.png';
  }
  
  // Fallback for cards without cardType (legacy cards)
  const rivalryType = card.rivalryType;
  const teamPreference = card.teamPreference;
  
  // Team name to abbreviation mapping
  const teamAbbreviations = {
    'Red Bull': 'rb',
    'Racing Bulls': 'rb',
    'Ferrari': 'ferrari',
    'McLaren': 'mclaren',
    'Mercedes': 'mercedes',
    'Aston Martin': 'aston',
    'Haas': 'haas',
    'Alpine': 'alpine',
    'Kick Sauber': 'sauber',
    'Williams': 'williams',
    'Any team': 'any'
  };
  
  switch (rivalryType) {
    case 'Teammates':
      const teamAbbr = teamAbbreviations[teamPreference] || 'any';
      return `/images/teammates-card-image-${teamAbbr}.jpg`;
    case 'Rival Teams':
    case 'Rival teams':
    case 'rival teams':
    case 'Any rivalry':
    case 'Any Rivalry':
    case 'any rivalry':
    default:
      return '/images/rival-card-image.jpg';
  }
};

// Helper to format date as DD/MM/YYYY
function formatDateDDMMYYYY(date) {
  if (!date) return 'Unknown';
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Helper for team badge image (reuse previous mapping)
const teamBadgeMap = {
  'Alpine': 'team-alp.png',
  'Aston Martin': 'team-am.png',
  'Ferrari': 'team-fer.png',
  'Haas': 'team-haas.png',
  'Kick Sauber': 'team-kick.png',
  'McLaren': 'team-mcl.png',
  'Mercedes': 'team-merc.png',
  'Red Bull': 'team-rb.png',
  'Racing Bulls': 'team-visa.png',
  'Williams': 'team-will.png',
};
function TeamBadgeInline({ team }) {
  const badge = teamBadgeMap[team];
  if (!badge) return null;
  return (
    <img
      src={`/images/team-badges/${badge}`}
      alt={`${team} F1 Team Badge`}
      style={{ height: 24, verticalAlign: 'middle', marginLeft: 8 }}
    />
  );
}

function RaceCards() {
  const [raceCards, setRaceCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [applyingCardId, setApplyingCardId] = useState(null);
  const [userApplications, setUserApplications] = useState([]);
  const [cardTypeFilter, setCardTypeFilter] = useState('all');
  const [careerTeamFilter, setCareerTeamFilter] = useState('all');
  const [careerAiLevelFilter, setCareerAiLevelFilter] = useState('all');
  const [careerRivalryFilter, setCareerRivalryFilter] = useState('all');
  const [careerDistanceFilter, setCareerDistanceFilter] = useState('all');
  const [unrankedCarTypeFilter, setUnrankedCarTypeFilter] = useState('all');
  const [unrankedQualifyingFilter, setUnrankedQualifyingFilter] = useState('all');
  const [unrankedDamageFilter, setUnrankedDamageFilter] = useState('all');
  const [unrankedDistanceFilter, setUnrankedDistanceFilter] = useState('all');
  const [reserveTrackFilter, setReserveTrackFilter] = useState('all');
  const [reserveAiLevelFilter, setReserveAiLevelFilter] = useState('all');

  const [reserveDistanceFilter, setReserveDistanceFilter] = useState('all');
  const [reservePerformanceFilter, setReservePerformanceFilter] = useState('all');
  const [showAllFilters, setShowAllFilters] = useState(false);
  const { isAuthenticated, user, isEmailVerified, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const CARDS_PER_PAGE = isMobile ? 9 : 21;
  const MAX_PAGES = 10;
  const [confirmClose, setConfirmClose] = useState({ open: false, application: null });

  const trackSponsorClick = () => trackEvent('flr_sponsor_click', { event_category: 'engagement', event_label: 'discord_invite_race_cards' });

  // Career card filter options
  const careerTeamOptions = [
    'Any team',
    'Red Bull',
    'Racing Bulls',
    'Ferrari',
    'McLaren',
    'Mercedes',
    'Aston Martin',
    'Haas',
    'Alpine',
    'Kick Sauber',
    'Williams'
  ];

  const careerAiLevelOptions = [
    '0-40',
    '40-50',
    '60-70',
    '70-80',
    '80-90',
    '90-100',
    '100+'
  ];

  const leagueStartupAiLevelOptions = ['Multiple Tiers', ...careerAiLevelOptions];

  const careerRivalryTypeOptions = [
    'Any rivalry',
    'Teammates',
    'Rival Teams'
  ];

  const careerRaceDistanceOptions = [
    '5 laps',  
    '25%',
    '50%',
    '100%'
  ];

  // Unranked card filter options
  const unrankedCarTypeOptions = [
    'F1',
    'F2',
    'F1 World'
  ];

  const unrankedQualifyingOptions = [
    'No Quali',
    'One Shot',
    'Short Quali',
    'Full Quali'
  ];

  const unrankedDamageOptions = [
    'Damage On',
    'Damage Off'
  ];

  const unrankedRaceDistanceOptions = [
    '5 laps',
    '25%',
    '35%',
    '50%',
    '100%'
  ];

  // Reserve card filter options (all tracks across game versions)
  const reserveTrackOptions = ALL_TRACK_OPTIONS;

  const reserveAiLevelOptions = [
    '0-40',
    '40-50',
    '60-70',
    '70-80',
    '80-90',
    '90-100',
    '100+'
  ];



  const reserveRaceDistanceOptions = [
    '5 laps',  
    '25%',
    '50%',
    '100%'
  ];

  const reservePerformanceOptions = [
    'Equal',
    'Realistic'
  ];

  useEffect(() => {
    if (authLoading) {
      // Wait for the authentication status to be resolved before fetching data
      return;
    }

    let unsubscribe = null;

    const setupRaceCards = () => {
      console.log('Setting up race cards listener for user:', user?.uid);
      
      unsubscribe = getActiveRaceCards((cards) => {
        console.log('Received race cards:', cards);
        console.log('Current user:', user?.uid);
        setRaceCards(cards);
        setLoading(false);
      });
    };

    setupRaceCards();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      cleanupRaceCardListeners();
    };
  }, [authLoading, user]);

  // Fetch applications for the current user
  useEffect(() => {
    if (user && isAuthenticated()) {
      const fetchUserApplications = async () => {
        const q = query(
          collection(db, 'raceCardApplications'),
          where('applicantId', '==', user.uid)
        );
        const snapshot = await getDocs(q);
        setUserApplications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      };
      fetchUserApplications();
    }
  }, [user, isAuthenticated]);

  const appliedCardIds = userApplications.map(app => app.raceCardId);

  const handleApply = async (cardId) => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!isEmailVerified()) {
      setError('Please verify your email before applying to race cards.');
      return;
    }

    // Count active applications (pending or accepted)
    const activeApplications = userApplications.filter(app => app.status === 'pending' || app.status === 'accepted');
    if (activeApplications.length >= 3) {
      setError('You can only have 3 active applications. Please close a card to apply for more.');
      return;
    }

    // Check if the card already has 5 applications
    const card = raceCards.find(c => c.id === cardId);
    const cardActiveAppCount = card && card.applications ? card.applications.filter(app => app.status === 'pending' || app.status === 'accepted').length : 0;
    if (cardActiveAppCount >= 5) {
      setError('This card has reached the maximum number of applications. Please try another card.');
      return;
    }

    setApplyingCardId(cardId);
    setError('');
    setSuccess('');

    try {
      console.log('Current user ID:', user.uid);
      
      // Get user's EA ID from their profile
      const userProfile = await getUserProfile(user.uid);
      console.log('User profile data:', userProfile);
      
      if (!userProfile) {
        console.log('No user profile found');
        setError('User profile not found. Please try logging out and back in.');
        setApplyingCardId(null);
        return;
      }
      
      if (!userProfile.eaId) {
        console.log('User profile found but no EA ID:', userProfile);
        console.log('Available fields in user profile:', Object.keys(userProfile));
        console.log('Full user profile data:', JSON.stringify(userProfile, null, 2));
        console.log('Checking for eaID field specifically:', userProfile.eaID);
        console.log('Checking for eaId field (lowercase):', userProfile.eaId);
        console.log('Checking for EA_ID field:', userProfile.EA_ID);
        setError('Please complete your profile with your EA ID before applying to race cards.');
        setApplyingCardId(null);
        return;
      }

      console.log('EA ID found:', userProfile.eaId);
      await applyToRaceCard(cardId, user.uid, userProfile.eaId);
      setSuccess('Application submitted successfully!');
      
      // Refresh user applications to show the new application immediately
      const q = query(
        collection(db, 'raceCardApplications'),
        where('applicantId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      setUserApplications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Scroll to "Your Applications" section after successful application
      setTimeout(() => {
        const applicationsSection = document.getElementById('your-applications-section');
        if (applicationsSection) {
          applicationsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // If the section doesn't exist, scroll to top of page
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100); // Small delay to ensure the DOM has updated
    } catch (error) {
      console.error('Error applying to race card:', error);
      setError(error.message || 'Failed to apply to race card');
    } finally {
      setApplyingCardId(null);
    }
  };

  const isUserCard = (card) => {
    return user && card.userId === user.uid;
  };

  const hasApplied = (cardId) => appliedCardIds.includes(cardId);

  // Split race cards into applied and not applied
  // Instead of filtering only by active cards, include accepted applications even if the card is not active
  const appliedCards = userApplications
    .map(app => {
      const card = raceCards.find(c => c.id === app.raceCardId);
      // Use stored card data from application if original card is not available
      const displayCard = card || app.cardData;
      return { application: app, card: displayCard };
    })
    .filter(({ application, card }) => {
      if (!card && application.status === 'accepted') {
        // If the card is not in active cards but the application is accepted, show it
        return true;
      }
      if (card) {
        if (application.status === 'accepted') return true;
        if (card.status === 'active') return true;
      }
      return false;
    });
  const otherCards = raceCards.filter(card => {
    
    // Apply card type filter
    if (cardTypeFilter === 'all') return true;
    if (cardTypeFilter === 'career' && card.cardType === 'career') {
      // Apply additional career card filters
      if (careerTeamFilter !== 'all' && card.teamPreference !== careerTeamFilter) return false;
      if (careerAiLevelFilter !== 'all' && card.aiLevel !== careerAiLevelFilter) return false;
      if (careerRivalryFilter !== 'all' && card.rivalryType !== careerRivalryFilter) return false;
      if (careerDistanceFilter !== 'all' && card.raceDistance !== careerDistanceFilter) return false;
      return true;
    }
    if (cardTypeFilter === 'leagueStartup' && card.cardType === 'leagueStartup') {
      // League Startup: only AI level and distance (no team or rivalry)
      if (careerAiLevelFilter !== 'all' && card.aiLevel !== careerAiLevelFilter) return false;
      if (careerDistanceFilter !== 'all' && card.raceDistance !== careerDistanceFilter) return false;
      return true;
    }
    if (cardTypeFilter === 'unranked' && card.cardType === 'unranked') {
      // Apply additional unranked card filters
      if (unrankedCarTypeFilter !== 'all' && card.carType !== unrankedCarTypeFilter) return false;
      if (unrankedQualifyingFilter !== 'all' && card.qualifyingPreference !== unrankedQualifyingFilter) return false;
      if (unrankedDamageFilter !== 'all' && card.damage !== unrankedDamageFilter) return false;
      if (unrankedDistanceFilter !== 'all' && card.raceDistance !== unrankedDistanceFilter) return false;
      return true;
    }
    if (cardTypeFilter === 'reserve' && card.cardType === 'reserve') {
      // Apply additional reserve card filters
      if (reserveTrackFilter !== 'all' && card.track !== reserveTrackFilter) return false;
      if (reserveAiLevelFilter !== 'all' && card.estAiLevel !== reserveAiLevelFilter) return false;

      if (reserveDistanceFilter !== 'all' && card.raceDistance !== reserveDistanceFilter) return false;
      if (reservePerformanceFilter !== 'all' && card.performance !== reservePerformanceFilter) return false;
      return true;
    }
    
    return false;
  });

  // Pagination logic for otherCards
  const totalOtherCards = otherCards.length;
  const totalPages = Math.min(Math.ceil(totalOtherCards / CARDS_PER_PAGE), MAX_PAGES);
  const paginatedOtherCards = otherCards.slice(
    (currentPage - 1) * CARDS_PER_PAGE,
    currentPage * CARDS_PER_PAGE
  );

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [cardTypeFilter, careerTeamFilter, careerAiLevelFilter, careerRivalryFilter, careerDistanceFilter, 
      unrankedCarTypeFilter, unrankedQualifyingFilter, unrankedDamageFilter, unrankedDistanceFilter,
      reserveTrackFilter, reserveAiLevelFilter, reserveDistanceFilter, reservePerformanceFilter]);

  // Reset career filters when card type changes
  useEffect(() => {
    if (cardTypeFilter !== 'career' && cardTypeFilter !== 'leagueStartup') {
      setCareerTeamFilter('all');
      setCareerAiLevelFilter('all');
      setCareerRivalryFilter('all');
      setCareerDistanceFilter('all');
    }
    if (cardTypeFilter !== 'unranked') {
      setUnrankedCarTypeFilter('all');
      setUnrankedQualifyingFilter('all');
      setUnrankedDamageFilter('all');
      setUnrankedDistanceFilter('all');
    }
    if (cardTypeFilter !== 'reserve') {
          setReserveTrackFilter('all');
    setReserveAiLevelFilter('all');

    setReserveDistanceFilter('all');
    setReservePerformanceFilter('all');
    }
  }, [cardTypeFilter]);

  if (loading || authLoading) {
    return (
      <Container maxWidth="lg" className={styles.loadingContainer}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" className={styles.containerMain}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className={styles.mainContainer}>
      {appliedCards.length > 0 && (
        <Paper elevation={3} className={styles.paperApplied} id="your-applications-section">
          <Typography variant="h5" component="h2" gutterBottom className={styles.cardTitleApplied}>
            Your Applications
          </Typography>
          <Grid container spacing={3} className={styles.gridStretch}>
            {appliedCards.map(({ card, application }) => {
              const acceptedDiscordRaw = String(
                card?.discordLink || application.cardData?.discordLink || ''
              ).trim();
              const acceptedDiscordHref = acceptedDiscordRaw ? externalInviteHref(acceptedDiscordRaw) : '';

              // If card is missing (not active), use minimal info from application
              let statusLabel = 'Pending';
              let statusColor = 'warning';
              if (application) {
                if (application.status === 'accepted') {
                  statusLabel = `Accepted – ${application.cardCreatorEaId} will now add you on EA`;
                  statusColor = 'success';
                } else if (application.status === 'rejected') {
                  statusLabel = 'Application rejected/filled';
                  statusColor = 'error';
                } else if (application.status === 'pending') {
                  const cardIsActive = card && card.status === 'active';
                  const anotherAccepted = card && card.applications && card.applications.some(app => app.status === 'accepted' && app.applicantId !== user.uid);
                  if (!cardIsActive || anotherAccepted) {
                    statusLabel = 'Application rejected/filled';
                    statusColor = 'error';
                  }
                }
              }
              // Force 3 cards per row on desktop, 1 per row on mobile/tablet
              return (
                <Grid item xs={12} md={4} key={application.raceCardId} className={styles.gridItemFlex}>
                  <Card elevation={6} className={styles.raceCard}>
                    <CardContent className={styles.cardContentFlex}>
                      <Typography variant="h5" component="h2" gutterBottom className={styles.cardTitle}>
                        {card ? card.title : (card?.cardType === 'career' ? '2 Player Career' : 
                                             card?.cardType === 'unranked' ? 'Unranked Lobby' : 
                                             card?.cardType === 'reserve' ? 'Reserve a Race' : 
                                             card?.cardType === 'commentary' ? 'Commentary' : 
                                             card?.cardType === 'leagueStartup' ? 'League Startup' : '2 Player Career')}
                        {(card.cardType === 'career' || card.cardType === 'leagueStartup') && card.teamPreference && card.rivalryType === 'Teammates' && <TeamBadgeInline team={card.teamPreference} />}
                      </Typography>
                      {application && application.status === 'accepted' ? (
                        <Box className={styles.applicationAccepted}>
                          <Typography variant="body1" color="success.dark" sx={{ fontWeight: 'medium' }}>
                            Accepted – <Typography component="span" variant="body1" color="success.dark" sx={{ fontWeight: 'bold' }}>{application.cardCreatorEaId}</Typography> will now add you on EA.
                            {acceptedDiscordRaw ? (
                              <>
                                {' '}or you can join their league Discord{' '}
                                <Link
                                  href={acceptedDiscordHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  color="inherit"
                                  sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
                                >
                                  here
                                </Link>
                                .
                              </>
                            ) : null}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip label={statusLabel} color={statusColor} className={styles.chipStatus} />
                      )}
                      {/* Race Preferences Section */}
                      {card && (
                        <>
                          <Box className={styles.sectionBoxApplied}>
                            <Typography variant="subtitle1" className={styles.sectionTitleApplied}>
                              {card.cardType === 'career' ? 'Race Preferences:' : 
                               card.cardType === 'unranked' ? 'Lobby Preferences:' :
                               card.cardType === 'reserve' ? 'Race Details:' : 
                               card.cardType === 'leagueStartup' ? 'League Preferences:' : 
                               card.cardType === 'commentary' ? 'Race Details:' : 'Race Preferences:'}
                            </Typography>
                            <Box className={styles.chipsContainerApplied}>
                              {/* Game version (when set) */}
                              {card.gameVersion && (
                                <Chip
                                  label={GAME_VERSIONS.find(g => g.value === card.gameVersion)?.label || card.gameVersion}
                                  size="medium"
                                  sx={{ bgcolor: '#c00', color: 'white' }}
                                />
                              )}
                              {/* Career Card Fields */}
                              {(card.cardType === 'career' || card.cardType === 'leagueStartup') && (
                                <>
                                  {card.cardType === 'career' && (
                                    <>
                                      <Chip
                                        label={card.teamPreference}
                                        color="primary"
                                        size="medium"
                                        sx={{ 
                                          bgcolor: teamColors[card.teamPreference]?.bg || '#000000',
                                          color: teamColors[card.teamPreference]?.color || '#ffffff',
                                          ...(card.teamPreference === 'Haas' ? { border: '2px solid #FF0000' } : {})
                                        }}
                                      />
                                      <Chip
                                        label={card.rivalryType}
                                        color="primary"
                                        size="medium"
                                        sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                      />
                                    </>
                                  )}
                                  <Chip
                                    label={(card.cardType === 'leagueStartup' && card.aiLevel === 'Multiple Tiers') ? 'Multiple Tiers' : `${card.aiLevel} AI`}
                                    color="primary"
                                    size="medium"
                                    variant={card.cardType === 'leagueStartup' ? 'filled' : 'outlined'}
                                    sx={card.cardType === 'leagueStartup' ? { bgcolor: 'black', color: 'white' } : { borderColor: 'black', color: 'black' }}
                                  />
                                  <Chip
                                    label={card.raceDistance}
                                    color="primary"
                                    size="medium"
                                    variant="outlined"
                                    sx={{ borderColor: 'black', color: 'black' }}
                                  />
                                  {card.cardType === 'leagueStartup' && card.qualifyingFormat && (
                                    <Chip
                                      label={card.qualifyingFormat}
                                      color="primary"
                                      size="medium"
                                      sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                    />
                                  )}
                                  {card.cardType === 'leagueStartup' && card.damage && (
                                    <Chip
                                      label={card.damage}
                                      color="primary"
                                      size="medium"
                                      sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                    />
                                  )}
                                  {card.cardType === 'leagueStartup' && card.assists && (
                                    <Chip
                                      label={card.assists}
                                      color="primary"
                                      size="medium"
                                      sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                    />
                                  )}
                                </>
                              )}

                              {/* Unranked Card Fields */}
                              {card.cardType === 'unranked' && (
                                <>
                                  <Chip
                                    label={card.carType}
                                    color="primary"
                                    size="medium"
                                    sx={{ bgcolor: 'black', color: 'white' }}
                                  />
                                  <Chip
                                    label={card.qualifyingPreference}
                                    color="primary"
                                    size="medium"
                                    variant="outlined"
                                    sx={{ borderColor: 'black', color: 'black' }}
                                  />
                                  <Chip
                                    label={card.damage}
                                    color="primary"
                                    size="medium"
                                    sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                  />
                                  <Chip
                                    label={card.raceDistance}
                                    color="primary"
                                    size="medium"
                                    variant="outlined"
                                    sx={{ borderColor: 'black', color: 'black' }}
                                  />
                                </>
                              )}

                              {/* Reserve/Commentary Card Fields */}
                              {(card.cardType === 'reserve' || card.cardType === 'commentary') && (
                                <>
                                  <Chip
                                    label={card.track}
                                    color="primary"
                                    size="medium"
                                    sx={{ bgcolor: 'black', color: 'white' }}
                                  />
                                  {card.cardType === 'reserve' && (
                                    <Chip
                                      label={`${card.estAiLevel} AI`}
                                      color="primary"
                                      size="medium"
                                      variant="outlined"
                                      sx={{ borderColor: 'black', color: 'black' }}
                                    />
                                  )}
                                  <Chip
                                    label={card.raceDistance}
                                    color="primary"
                                    size="medium"
                                    sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                  />

                                  <Chip
                                    label={card.qualifyingFormat}
                                    color="primary"
                                    size="medium"
                                    sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                  />
                                  {card.seats && card.cardType === 'reserve' && (
                                    <Chip
                                      label={`${card.seats} ${card.seats === 1 ? 'seat' : 'seats'}`}
                                      color="primary"
                                      size="medium"
                                      sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                    />
                                  )}
                                </>
                              )}

                              {/* Fallback for cards without cardType */}
                              {!card.cardType && (
                                <>
                                  <Chip
                                    label={card.teamPreference || 'Unknown'}
                                    color="primary"
                                    size="medium"
                                    sx={{ 
                                      bgcolor: teamColors[card.teamPreference]?.bg || '#000000',
                                      color: teamColors[card.teamPreference]?.color || '#ffffff',
                                      ...((card.teamPreference || 'Unknown') === 'Haas' ? { border: '2px solid #FF0000' } : {})
                                    }}
                                  />
                                  <Chip
                                    label={`${card.aiLevel || 'Unknown'} AI`}
                                    color="primary"
                                    size="medium"
                                    variant="outlined"
                                    sx={{ borderColor: 'black', color: 'black' }}
                                  />
                                  <Chip
                                    label={card.rivalryType || 'Unknown'}
                                    color="primary"
                                    size="medium"
                                    sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                  />
                                  <Chip
                                    label={card.raceDistance || 'Unknown'}
                                    color="primary"
                                    size="medium"
                                    variant="outlined"
                                    sx={{ borderColor: 'black', color: 'black' }}
                                  />
                                </>
                              )}
                            </Box>
                          </Box>
                          {/* Schedule Preferences Section */}
                          <Box className={styles.sectionBoxApplied}>
                            <Typography variant="subtitle1" className={styles.sectionTitleApplied}>
                              {card.cardType === 'career' ? 'Schedule Preferences:' : 
                               card.cardType === 'unranked' ? 'Lobby Schedule:' :
                               card.cardType === 'reserve' || card.cardType === 'commentary' ? 'Race Schedule:' : 
                               card.cardType === 'leagueStartup' ? 'Schedule Preferences:' : 'Schedule Preferences:'}
                            </Typography>
                            <Box className={styles.chipsContainerApplied}>
                              <Chip
                                label={card.timezone}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'black', color: 'white' }}
                              />
                              <Chip
                                label={card.cardType === 'career' ? card.timeOfDay :
                                       card.cardType === 'leagueStartup' ? formatTimeWithAMPM(card.timeOfDay, card.timezone) :
                                       card.cardType === 'unranked' ? formatTimeWithAMPM(card.estimatedStartTime, card.timezone) :
                                       (card.cardType === 'reserve' || card.cardType === 'commentary') ? formatTimeWithAMPM(card.startTime, card.timezone) :
                                       card.timeOfDay}
                                color="primary"
                                size="medium"
                                variant="outlined"
                                sx={{ borderColor: 'black', color: 'black' }}
                              />
                              <Chip
                                label={card.cardType === 'career' ? card.frequency :
                                       card.cardType === 'leagueStartup' ? `Races: ${card.races ?? ''}` :
                                       card.cardType === 'unranked' ? card.runTime :
                                       (card.cardType === 'reserve' || card.cardType === 'commentary') ? (card.raceDate ? formatDateDDMMYYYY(card.raceDate.seconds ? card.raceDate.seconds * 1000 : card.raceDate) : 'Unknown') :
                                       card.frequency}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                              {card.cardType === 'unranked' && card.raceDate && (
                                <Chip
                                  label={formatDateDDMMYYYY(card.raceDate.seconds ? card.raceDate.seconds * 1000 : card.raceDate)}
                                  color="primary"
                                  size="medium"
                                  sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                />
                              )}
                              {card.cardType === 'leagueStartup' && card.leagueStartDate && (
                                <Chip
                                  label={`Start: ${formatDateDDMMYYYY(card.leagueStartDate?.seconds ? card.leagueStartDate.seconds * 1000 : card.leagueStartDate)}`}
                                  color="primary"
                                  size="medium"
                                  sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                />
                              )}
                              {(card.cardType === 'career' || card.cardType === 'leagueStartup') && card.preferredDays && 
                               Object.entries(card.preferredDays)
                                .filter(([_, selected]) => selected)
                                .map(([day], index) => (
                                  <Chip
                                    key={day}
                                    label={day}
                                    color="primary"
                                    size="medium"
                                    variant={index % 2 === 0 ? "outlined" : "filled"}
                                    sx={index % 2 === 0 
                                      ? { borderColor: 'black', color: 'black' }
                                      : { bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }
                                    }
                                  />
                                ))}
                              {(card.cardType === 'reserve' || card.cardType === 'commentary') && card.fullTimeSeat && (
                                <Chip
                                  label={card.cardType === 'commentary' ? 'Paid Role' : 'Full Time Seat'}
                                  color="primary"
                                  size="medium"
                                  sx={{ bgcolor: 'green', color: 'white' }}
                                />
                              )}
                              {card.cardType === 'unranked' && card.streamingPlatform === 'YouTube' && (
                                <a href={card.streamLink} target="_blank" rel="noopener noreferrer" className={styles.streamLink}>
                                  <img src="/images/youtube-logo.png" alt="YouTube" className={styles.youtubeLogo} />
                                </a>
                              )}
                              {card.cardType === 'unranked' && card.streamingPlatform === 'Twitch' && (
                                <a href={card.streamLink} target="_blank" rel="noopener noreferrer" className={styles.streamLink}>
                                  <img src="/images/twitch-logo.png" alt="Twitch" className={styles.twitchLogo} />
                                </a>
                              )}
                              {(card.cardType === 'reserve' || card.cardType === 'commentary' || card.cardType === 'leagueStartup') && card.streamingPlatform === 'YouTube' && (
                                <a href={card.streamLink} target="_blank" rel="noopener noreferrer" className={styles.streamLink}>
                                  <img src="/images/youtube-logo.png" alt="YouTube" className={styles.youtubeLogo} />
                                </a>
                              )}
                              {(card.cardType === 'reserve' || card.cardType === 'commentary' || card.cardType === 'leagueStartup') && card.streamingPlatform === 'Twitch' && (
                                <a href={card.streamLink} target="_blank" rel="noopener noreferrer" className={styles.streamLink}>
                                  <img src="/images/twitch-logo.png" alt="Twitch" className={styles.twitchLogo} />
                                </a>
                              )}
                              {(card.cardType === 'leagueStartup' || card.cardType === 'reserve' || card.cardType === 'commentary') && card.discordLink && (
                                <a href={card.discordLink} target="_blank" rel="noopener noreferrer" className={styles.streamLink}>
                                  <img src="/images/discord-logo.png" alt="Discord" className={styles.discordLogo} />
                                </a>
                              )}
                            </Box>
                          </Box>
                        </>
                      )}
                    </CardContent>
                    <CardActions className={styles.cardActionsEnd}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={async () => {
                          if (application.status === 'accepted') {
                            setConfirmClose({ open: true, application });
                          } else {
                            await deleteApplicationForRaceCard(application.raceCardId, user.uid);
                            setUserApplications(prev => prev.filter(app => app.raceCardId !== application.raceCardId));
                          }
                        }}
                        className={styles.buttonMinWidth}
                      >
                        Close Card
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}
      <Paper elevation={3} className={styles.paperFilter}>
        <Box className={styles.filterContainer}>
          <Typography variant="h4" component="h2" className={styles.filterTitle}>
            Active Race Cards
          </Typography>
          <Box className={styles.filterContainer}>
          <FormControl className={styles.filterControlWide}>
            <Select
              value={cardTypeFilter}
              onChange={(e) => setCardTypeFilter(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="all">All Card Types</MenuItem>
              <MenuItem value="career">2 Player Career</MenuItem>
              <MenuItem value="leagueStartup">League Startup</MenuItem>
              <MenuItem value="unranked">Unranked Lobby</MenuItem>
              <MenuItem value="reserve">Reserve a Race</MenuItem>
            </Select>
          </FormControl>
          </Box>
          
          {/* Show "See More Filters" toggle on mobile when filters are available */}
          {isMobile && cardTypeFilter !== 'all' && (
            <Box sx={{ mt: 2, mb: 2, pl: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllFilters}
                    onChange={(e) => setShowAllFilters(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                }
                label="Show more filters"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary'
                  }
                }}
              />
            </Box>
          )}
          
          {/* Career card specific filters */}
          {cardTypeFilter === 'career' && (!isMobile || showAllFilters) && (
            <Box className={styles.filterRow}>
              <FormControl className={styles.filterControl}>
                <Select
                  value={careerTeamFilter}
                  onChange={(e) => setCareerTeamFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Teams</MenuItem>
                  {careerTeamOptions.map((team) => (
                    <MenuItem key={team} value={team}>{team}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={careerAiLevelFilter}
                  onChange={(e) => setCareerAiLevelFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All AI Levels</MenuItem>
                  {careerAiLevelOptions.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={careerRivalryFilter}
                  onChange={(e) => setCareerRivalryFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Rivalry Types</MenuItem>
                  {careerRivalryTypeOptions.map((rivalry) => (
                    <MenuItem key={rivalry} value={rivalry}>{rivalry}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={careerDistanceFilter}
                  onChange={(e) => setCareerDistanceFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Distances</MenuItem>
                  {careerRaceDistanceOptions.map((distance) => (
                    <MenuItem key={distance} value={distance}>{distance}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {/* League Startup filters (AI Level + Distance only) */}
          {cardTypeFilter === 'leagueStartup' && (!isMobile || showAllFilters) && (
            <Box className={styles.filterRow}>
              <FormControl className={styles.filterControl}>
                <Select
                  value={careerAiLevelFilter}
                  onChange={(e) => setCareerAiLevelFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All AI Levels</MenuItem>
                  {leagueStartupAiLevelOptions.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={careerDistanceFilter}
                  onChange={(e) => setCareerDistanceFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Distances</MenuItem>
                  {careerRaceDistanceOptions.map((distance) => (
                    <MenuItem key={distance} value={distance}>{distance}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {/* Unranked card specific filters */}
          {cardTypeFilter === 'unranked' && (!isMobile || showAllFilters) && (
            <Box className={styles.filterRow}>
              <FormControl className={styles.filterControl}>
                <Select
                  value={unrankedCarTypeFilter}
                  onChange={(e) => setUnrankedCarTypeFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Car Types</MenuItem>
                  {unrankedCarTypeOptions.map((carType) => (
                    <MenuItem key={carType} value={carType}>{carType}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={unrankedQualifyingFilter}
                  onChange={(e) => setUnrankedQualifyingFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Qualifying</MenuItem>
                  {unrankedQualifyingOptions.map((qualifying) => (
                    <MenuItem key={qualifying} value={qualifying}>{qualifying}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={unrankedDamageFilter}
                  onChange={(e) => setUnrankedDamageFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Damage Settings</MenuItem>
                  {unrankedDamageOptions.map((damage) => (
                    <MenuItem key={damage} value={damage}>{damage}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={unrankedDistanceFilter}
                  onChange={(e) => setUnrankedDistanceFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Distances</MenuItem>
                  {unrankedRaceDistanceOptions.map((distance) => (
                    <MenuItem key={distance} value={distance}>{distance}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {/* Reserve card specific filters */}
          {cardTypeFilter === 'reserve' && (!isMobile || showAllFilters) && (
            <Box className={styles.filterRow}>
              <FormControl className={styles.filterControl}>
                <Select
                  value={reserveTrackFilter}
                  onChange={(e) => setReserveTrackFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Tracks</MenuItem>
                  {reserveTrackOptions.map((track) => (
                    <MenuItem key={track} value={track}>{track}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={reserveAiLevelFilter}
                  onChange={(e) => setReserveAiLevelFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All AI Levels</MenuItem>
                  {reserveAiLevelOptions.map((level) => (
                    <MenuItem key={level} value={level}>{level}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl className={styles.filterControl}>
                <Select
                  value={reserveDistanceFilter}
                  onChange={(e) => setReserveDistanceFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Distances</MenuItem>
                  {reserveRaceDistanceOptions.map((distance) => (
                    <MenuItem key={distance} value={distance}>{distance}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={styles.filterControl}>
                <Select
                  value={reservePerformanceFilter}
                  onChange={(e) => setReservePerformanceFilter(e.target.value)}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="all">All Performance</MenuItem>
                  {reservePerformanceOptions.map((performance) => (
                    <MenuItem key={performance} value={performance}>{performance}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Sponsor Banner */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: { xs: 'center', md: 'flex-start' },
        width: '100%',
        mt: { xs: 3, md: 4 },
        mb: { xs: 2, md: 4 },
        px: { xs: 0.25, md: 0 }
      }}>
        <a 
          href="https://discord.gg/NTGCabTJkA" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'block' }}
          onClick={trackSponsorClick}
        >
                <img 
                  src="/images/sponsor-banner.png" 
                  alt="Join RaceWithMe Discord Community - F1 Game Racing Partners" 
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    maxWidth: '550px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'pointer'
                  }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          />
        </a>
      </Box>
      
      <Grid container spacing={3} className={styles.gridStretch}>
        {paginatedOtherCards.length === 0 ? (
          <Grid item xs={12}>
            <Box className={styles.emptyState}>
              <Typography variant="h6" color="text.secondary">
                No active race cards
              </Typography>
            </Box>
          </Grid>
        ) : (
          <>
            {paginatedOtherCards.map((card, index) => {
              // Count active applications for this card
              const activeAppCount = card.applications ? card.applications.filter(app => app.status === 'pending' || app.status === 'accepted').length : 0;
              const maxApplications = card.cardType === 'career' ? 5 : card.cardType === 'leagueStartup' ? 60 : (card.cardType === 'unranked' || card.cardType === 'reserve' || card.cardType === 'commentary' ? 22 : 5);
              const isFull = activeAppCount >= maxApplications;
              return (
                <Grid item key={card.id} className={styles.gridItemFlex}>
                  <Card elevation={6} className={styles.raceCard}>
                  <CardContent className={styles.cardContentFlex}>
                    <Typography variant="h5" component="h2" gutterBottom className={styles.cardTitle}>
                      {card.title}
                      {(card.cardType === 'career' || card.cardType === 'leagueStartup') && card.teamPreference && card.rivalryType === 'Teammates' && <TeamBadgeInline team={card.teamPreference} />}
                    </Typography>
                    
                    {/* Dynamic Race Card Image */}
                    <Box className={card.cardType === 'commentary' ? styles.raceCardImageContainerBlack : styles.raceCardImageContainer}>
                      <img 
                        src={getRaceCardImage(card)}
                        alt={getRaceCardImageAlt(card)}
                        className={
                          card.cardType === 'career' || card.cardType === 'leagueStartup' ? styles.raceCardImageCareer :
                          card.cardType === 'unranked' ? styles.raceCardImageUnranked :
                          card.cardType === 'reserve' ? styles.raceCardImageReserve :
                          card.cardType === 'commentary' ? styles.raceCardImageCommentary :
                          styles.raceCardImage
                        }
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback placeholder */}
                      <Box className={styles.raceCardImagePlaceholder}>
                        <Typography variant="body1" color="text.secondary">
                          Race Card Image
                        </Typography>
                      </Box>
                    </Box>

                    {/* Race Preferences Section */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                        {card.cardType === 'career' ? 'Race Preferences:' : 
                         card.cardType === 'unranked' ? 'Lobby Preferences:' :
                         card.cardType === 'leagueStartup' ? 'League Preferences:' :
                         (card.cardType === 'reserve' || card.cardType === 'commentary') ? 'Race Details:' : 'Race Preferences:'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {/* Game version (when set) */}
                        {card.gameVersion && (
                          <Chip
                            label={GAME_VERSIONS.find(g => g.value === card.gameVersion)?.label || card.gameVersion}
                            size="medium"
                            sx={{ bgcolor: '#c00', color: 'white' }}
                          />
                        )}
                        {/* Career Card Fields */}
                        {(card.cardType === 'career' || card.cardType === 'leagueStartup') && (
                          <>
                            {card.cardType === 'career' && (
                              <>
                                <Chip
                                  label={card.teamPreference}
                                  color="primary"
                                  size="medium"
                                  sx={{ 
                                    bgcolor: teamColors[card.teamPreference]?.bg || '#000000',
                                    color: teamColors[card.teamPreference]?.color || '#ffffff',
                                    ...(card.teamPreference === 'Haas' ? { border: '2px solid #FF0000' } : {})
                                  }}
                                />
                                <Chip
                                  label={card.rivalryType}
                                  color="primary"
                                  size="medium"
                                  sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                />
                              </>
                            )}
                            <Chip
                              label={(card.cardType === 'leagueStartup' && card.aiLevel === 'Multiple Tiers') ? 'Multiple Tiers' : `${card.aiLevel} AI`}
                              color="primary"
                              size="medium"
                              variant={card.cardType === 'leagueStartup' ? 'filled' : 'outlined'}
                              sx={card.cardType === 'leagueStartup' ? { bgcolor: 'black', color: 'white' } : { borderColor: 'black', color: 'black' }}
                            />
                            <Chip
                              label={card.raceDistance}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                            {card.cardType === 'leagueStartup' && card.qualifyingFormat && (
                              <Chip
                                label={card.qualifyingFormat}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                            {card.cardType === 'leagueStartup' && card.damage && (
                              <Chip
                                label={card.damage}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                            {card.cardType === 'leagueStartup' && card.assists && (
                              <Chip
                                label={card.assists}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                          </>
                        )}

                        {/* Unranked Card Fields */}
                        {card.cardType === 'unranked' && (
                          <>
                            <Chip
                              label={card.carType}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'black', color: 'white' }}
                            />
                            <Chip
                              label={card.qualifyingPreference}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                            <Chip
                              label={card.damage}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                            />
                            <Chip
                              label={card.raceDistance}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                          </>
                        )}

                        {/* Reserve Card Fields */}
                        {(card.cardType === 'reserve' || card.cardType === 'commentary') && (
                          <>
                            <Chip
                              label={card.track}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'black', color: 'white' }}
                            />
                            {card.cardType === 'reserve' && (
                              <Chip
                                label={`${card.estAiLevel} AI`}
                                color="primary"
                                size="medium"
                                variant="outlined"
                                sx={{ borderColor: 'black', color: 'black' }}
                              />
                            )}
                            <Chip
                              label={card.raceDistance}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                            />

                            <Chip
                              label={card.qualifyingFormat}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                            />
                            {card.seats && card.cardType === 'reserve' && (
                              <Chip
                                label={`${card.seats} ${card.seats === 1 ? 'seat' : 'seats'}`}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                          </>
                        )}

                        {/* Fallback for cards without cardType */}
                        {!card.cardType && (
                          <>
                            <Chip
                              label={card.teamPreference || 'Unknown'}
                              color="primary"
                              size="medium"
                              sx={{ 
                                bgcolor: teamColors[card.teamPreference]?.bg || '#000000',
                                color: teamColors[card.teamPreference]?.color || '#ffffff',
                                ...((card.teamPreference || 'Unknown') === 'Haas' ? { border: '2px solid #FF0000' } : {})
                              }}
                            />
                            <Chip
                              label={`${card.aiLevel || 'Unknown'} AI`}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                            <Chip
                              label={card.rivalryType || 'Unknown'}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                            />
                            <Chip
                              label={card.raceDistance || 'Unknown'}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* Schedule Preferences Section */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                        {card.cardType === 'career' ? 'Schedule Preferences:' : 
                         card.cardType === 'unranked' ? 'Lobby Schedule:' :
                         card.cardType === 'leagueStartup' ? 'Schedule Preferences:' :
                         (card.cardType === 'reserve' || card.cardType === 'commentary') ? 'Race Schedule:' : 'Schedule Preferences:'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          label={card.timezone}
                          color="primary"
                          size="medium"
                          sx={{ bgcolor: 'black', color: 'white' }}
                        />
                        <Chip
                          label={card.cardType === 'career' ? card.timeOfDay :
                                 card.cardType === 'leagueStartup' ? formatTimeWithAMPM(card.timeOfDay, card.timezone) :
                                 card.cardType === 'unranked' ? formatTimeWithAMPM(card.estimatedStartTime, card.timezone) :
                                       (card.cardType === 'reserve' || card.cardType === 'commentary') ? formatTimeWithAMPM(card.startTime, card.timezone) :
                                       card.timeOfDay}
                          color="primary"
                          size="medium"
                          variant="outlined"
                          sx={{ borderColor: 'black', color: 'black' }}
                        />
                        <Chip
                          label={card.cardType === 'career' ? card.frequency :
                                 card.cardType === 'leagueStartup' ? `Races: ${card.races ?? ''}` :
                                 card.cardType === 'unranked' ? card.runTime :
                                 (card.cardType === 'reserve' || card.cardType === 'commentary') ? (card.raceDate ? formatDateDDMMYYYY(card.raceDate.seconds ? card.raceDate.seconds * 1000 : card.raceDate) : 'Unknown') :
                                 card.frequency}
                          color="primary"
                          size="medium"
                          sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                        />
                        {card.cardType === 'unranked' && card.raceDate && (
                          <Chip
                            label={formatDateDDMMYYYY(card.raceDate.seconds ? card.raceDate.seconds * 1000 : card.raceDate)}
                            color="primary"
                            size="medium"
                            sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                          />
                        )}
                        {card.cardType === 'leagueStartup' && card.leagueStartDate && (
                          <Chip
                            label={`Start: ${formatDateDDMMYYYY(card.leagueStartDate?.seconds ? card.leagueStartDate.seconds * 1000 : card.leagueStartDate)}`}
                            color="primary"
                            size="medium"
                            sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                          />
                        )}
                        {(card.cardType === 'career' || card.cardType === 'leagueStartup') && card.preferredDays && 
                         Object.entries(card.preferredDays)
                          .filter(([_, selected]) => selected)
                          .map(([day], index) => (
                            <Chip
                              key={day}
                              label={day}
                              color="primary"
                              size="medium"
                              variant={index % 2 === 0 ? "outlined" : "filled"}
                              sx={index % 2 === 0 
                                ? { borderColor: 'black', color: 'black' }
                                : { bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }
                              }
                            />
                          ))}
                              {(card.cardType === 'reserve' || card.cardType === 'commentary') && card.fullTimeSeat && (
                          <Chip
                                  label={card.cardType === 'commentary' ? 'Paid Role' : 'Full Time Seat'}
                            color="primary"
                            size="medium"
                            sx={{ bgcolor: 'green', color: 'white' }}
                          />
                        )}
                        {card.cardType === 'unranked' && card.streamingPlatform === 'YouTube' && (
                          <a href={card.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/youtube-logo.png" alt="YouTube" style={{ height: 20, width: 90, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {card.cardType === 'unranked' && card.streamingPlatform === 'Twitch' && (
                          <a href={card.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/twitch-logo.png" alt="Twitch" style={{ height: 22, width: 60, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {(card.cardType === 'reserve' || card.cardType === 'commentary' || card.cardType === 'leagueStartup') && card.streamingPlatform === 'YouTube' && (
                          <a href={card.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/youtube-logo.png" alt="YouTube" style={{ height: 20, width: 90, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {(card.cardType === 'reserve' || card.cardType === 'commentary' || card.cardType === 'leagueStartup') && card.streamingPlatform === 'Twitch' && (
                          <a href={card.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/twitch-logo.png" alt="Twitch" style={{ height: 22, width: 60, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {(card.cardType === 'leagueStartup' || card.cardType === 'reserve' || card.cardType === 'commentary') && card.discordLink && (
                          <a href={card.discordLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/discord-logo.png" alt="Discord" style={{ height: 18, width: 100, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                    <ShareButton raceCard={card} variant="icon" size="small" />
                    {!isUserCard(card) ? (
                      <Button
                        variant="contained"
                        color={applyingCardId === card.id ? "info" : hasApplied(card.id) ? "inherit" : isFull ? "warning" : "primary"}
                        onClick={() => handleApply(card.id)}
                        disabled={applyingCardId === card.id || hasApplied(card.id) || isFull}
                        sx={{
                          minWidth: '120px',
                          ...(hasApplied(card.id) && {
                            bgcolor: 'success.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'success.dark', color: 'white' },
                          })
                        }}
                      >
                        {applyingCardId === card.id
                          ? <CircularProgress size={20} color="inherit" />
                          : hasApplied(card.id)
                            ? 'Applied'
                            : isFull
                              ? 'Full'
                              : 'Apply'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/create-card', { state: { scrollToActiveCards: true } })}
                        sx={{ minWidth: '120px' }}
                      >
                        Back to your Active Cards
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
              );
            })}
          </>
        )}
      </Grid>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              borderRadius: 2,
              backgroundColor: 'white',
              border: '1px solid #e0e0e0'
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, value) => setCurrentPage(value)}
              color="primary"
              siblingCount={isMobile ? 1 : 1}
              boundaryCount={isMobile ? 1 : 1}
              showFirstButton={!isMobile}
              showLastButton={!isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  minWidth: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px'
                }
              }}
            />
          </Paper>
        </Box>
      )}

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for accepted applications */}
      <Dialog open={confirmClose.open} onClose={() => setConfirmClose({ open: false, application: null })}>
        <DialogTitle>Withdraw Accepted Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Closing this card will withdraw your information from the card owner. Please make sure they have added you to EA if you still wish to race.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose({ open: false, application: null })} color="primary">
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmClose.application) {
                await deleteApplicationForRaceCard(confirmClose.application.raceCardId, user.uid);
                setUserApplications(prev => prev.filter(app => app.raceCardId !== confirmClose.application.raceCardId));
              }
              setConfirmClose({ open: false, application: null });
            }}
            color="error"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default RaceCards; 